(function () {
  const collapsedAddonGroups = new Set();
  const expandedCustomize = new Set();
  let sariBusy = false;

  // Shared with free-gift.liquid so the two scripts' cart writes never overlap
  // in flight — Shopify does not serialise concurrent AJAX cart writes, and an
  // overlapping request can silently reassign a line's key, orphaning any
  // add-on tied to that key. Idempotent: whichever script's IIFE runs first
  // defines it, the other reuses the same instance.
  window.CartMutex = window.CartMutex || (function () {
    let tail = Promise.resolve();
    return {
      run(fn) {
        const next = tail.then(fn, fn);
        tail = next.catch(function () {});
        return next;
      },
    };
  })();

  function cartFetch(url, options) {
    return window.CartMutex.run(function () { return fetch(url, options); });
  }

  function applyAddonGroupState(sariKey) {
    const collapsed = collapsedAddonGroups.has(sariKey);
    document.querySelectorAll(`tr[data-addon-group="${sariKey}"]`).forEach((row) => {
      row.classList.toggle('is-collapsed', collapsed);
    });
    const toggle = document.querySelector(`[data-toggle-addon-group="${sariKey}"]`);
    if (toggle) {
      toggle.setAttribute('aria-expanded', String(!collapsed));
    }
  }

  function cartChangeUrl() {
    return (window.routes && window.routes.cart_change_url) || '/cart/change.js';
  }

  function cartAddUrl() {
    return (window.routes && window.routes.cart_add_url) || '/cart/add.js';
  }

  function cartUpdateUrl() {
    return (window.routes && window.routes.cart_update_url) || '/cart/update.js';
  }

  function jsonHeaders() {
    return { 'Content-Type': 'application/json', Accept: 'application/json' };
  }

  function sectionIds() {
    const ids = [];
    if (document.getElementById('main-cart-items')) ids.push(document.getElementById('main-cart-items').dataset.id);
    if (document.getElementById('main-cart-footer')) ids.push(document.getElementById('main-cart-footer').dataset.id);
    if (document.getElementById('cart-icon-bubble')) ids.push('cart-icon-bubble');
    if (document.getElementById('cart-live-region-text')) ids.push('cart-live-region-text');
    if (document.getElementById('cart-notification-product')) ids.push('cart-notification-product');
    if (document.getElementById('cart-notification-button')) ids.push('cart-notification-button');
    return ids.filter(Boolean);
  }

  function sectionTargets(contextSariKey) {
    const targets = [];
    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartItems) targets.push({ id: 'main-cart-items', section: mainCartItems.dataset.id, selector: '.js-contents' });
    if (mainCartFooter) targets.push({ id: 'main-cart-footer', section: mainCartFooter.dataset.id, selector: '.js-contents' });
    if (document.getElementById('cart-icon-bubble')) {
      targets.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    }
    if (document.getElementById('cart-live-region-text')) {
      targets.push({ id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' });
    }
    if (document.getElementById('cart-notification-product') && contextSariKey) {
      const safeKey = contextSariKey.replace(/:/g, '--');
      targets.push({
        id: 'cart-notification-product',
        section: 'cart-notification-product',
        selector: `#cart-notification-product-${safeKey}`,
      });
    }
    return targets;
  }

  function getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  function applySections(sections, contextSariKey, itemCount) {
    if (!sections) return;
    sectionTargets(contextSariKey).forEach((t) => {
      const target = document.getElementById(t.id);
      if (!target || !sections[t.section]) return;
      const scoped = t.id === 'cart-notification-product' ? target : target.querySelector(t.selector) || target;
      try {
        scoped.innerHTML = getSectionInnerHTML(sections[t.section], t.selector);
      } catch (e) {
        /* selector not found in returned markup (e.g. item just removed), skip */
      }
    });

    if (typeof itemCount === 'number') {
      const cartItemsEl = document.querySelector('cart-items');
      if (cartItemsEl) cartItemsEl.classList.toggle('is-empty', itemCount === 0);
      const cartFooterEl = document.getElementById('main-cart-footer');
      if (cartFooterEl) cartFooterEl.classList.toggle('is-empty', itemCount === 0);
    }

    bindHandlers();
  }

  async function cartRemoveByKey(key) {
    if (!key) return null;
    const body = JSON.stringify({
      id: key,
      quantity: 0,
      sections: sectionIds(),
      sections_url: window.location.pathname,
    });
    const res = await cartFetch(cartChangeUrl(), { method: 'POST', headers: jsonHeaders(), body });
    if (!res.ok) throw new Error('cart change failed');
    return res.json();
  }

  async function removeLinkedAddons(sariKey) {
    // Ask the server for ground truth rather than trusting DOM state, which can
    // drift out of sync after rapid or overlapping add/remove actions.
    const res = await fetch('/cart.js');
    const cart = await res.json();
    const toRemove = cart.items.filter((i) => i.properties && i.properties._addon_for === sariKey);
    let last = null;
    for (const item of toRemove) {
      last = await cartRemoveByKey(item.key);
    }
    return last;
  }

  // Reconcile the add-on lines for one sari against its customization controls.
  async function syncSari(scope, sariKey) {
    if (sariBusy) return;
    sariBusy = true;
    scope.classList.add('is-loading');

    try {
      // Desired add-ons from the current control state.
      const desired = [];
      scope.querySelectorAll('[data-addon-checkbox]:checked').forEach((cb) => {
        if (cb.dataset.variantId) desired.push({ id: String(cb.dataset.variantId), type: cb.dataset.addonType });
      });
      const master = scope.querySelector('[data-blouse-master]');
      if (master && master.checked) {
        const radio = scope.querySelector('[data-addon-radio]:checked');
        if (radio && radio.dataset.variantId) {
          desired.push({ id: String(radio.dataset.variantId), type: radio.dataset.addonType });
        }
      }

      // Current add-ons for this sari, straight from the server.
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const current = cart.items
        .filter((i) => i.properties && i.properties._addon_for === sariKey)
        .map((i) => ({ id: String(i.variant_id), type: i.properties._addon_type, key: i.key }));

      // Visible "Customization for" label written to each add-on line so staff
      // can see the sari it belongs to in the Shopify admin order view.
      // sariKey is the sari's stable _unit token, not its (mutable) cart key —
      // match on that first, falling back to key for legacy lines with no unit.
      const sariLine = cart.items.find((i) => (i.properties && i.properties._unit) === sariKey) ||
        cart.items.find((i) => i.key === sariKey);
      const sariTitle = sariLine ? sariLine.product_title || sariLine.title : 'Sari';
      const unit = sariLine && sariLine.properties && sariLine.properties.Unit;
      const parentRef = unit ? `${sariTitle} (unit ${unit})` : sariTitle;

      const toRemove = current.filter((c) => !desired.some((d) => d.id === c.id));
      const toAdd = desired.filter((d) => !current.some((c) => c.id === d.id));

      if (!toRemove.length && !toAdd.length) return;

      for (const c of toRemove) {
        await cartFetch(cartChangeUrl(), {
          method: 'POST',
          headers: jsonHeaders(),
          body: JSON.stringify({ id: c.key, quantity: 0 }),
        });
      }
      for (const d of toAdd) {
        await cartFetch(cartAddUrl(), {
          method: 'POST',
          headers: jsonHeaders(),
          body: JSON.stringify({
            id: d.id,
            quantity: 1,
            properties: { _addon_for: sariKey, _addon_type: d.type, 'Customization for': parentRef },
          }),
        });
      }

      const result = await cartFetch(cartUpdateUrl(), {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ sections: sectionIds(), sections_url: window.location.pathname }),
      }).then((r) => r.json());

      applySections(result.sections, sariKey, result.item_count);
    } catch (e) {
      console.error(e);
    } finally {
      sariBusy = false;
      scope.classList.remove('is-loading');
    }
  }

  function bindHandlers() {
    // Self-heal a cart left non-interactive by a stray Dawn loading state.
    const mci = document.getElementById('main-cart-items');
    if (mci && mci.classList.contains('cart__items--disabled') && !mci.querySelector('.loading-overlay:not(.hidden)')) {
      mci.classList.remove('cart__items--disabled');
    }

    document.querySelectorAll('[data-toggle-addon-group]').forEach((toggle) => {
      const sariKey = toggle.dataset.toggleAddonGroup;
      applyAddonGroupState(sariKey);
      if (toggle.dataset.bound) return;
      toggle.dataset.bound = 'true';
      toggle.addEventListener('click', () => {
        if (collapsedAddonGroups.has(sariKey)) {
          collapsedAddonGroups.delete(sariKey);
        } else {
          collapsedAddonGroups.add(sariKey);
        }
        applyAddonGroupState(sariKey);
      });
    });

    document.querySelectorAll('[data-sari-row]').forEach((row) => {
      if (row.dataset.cleanupBound) return;
      row.dataset.cleanupBound = 'true';
      const sariKey = row.dataset.sariKey; // stable _unit token — for matching add-ons

      const removeLink = row.querySelector('[data-sari-remove]');
      if (removeLink) {
        removeLink.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          removeLink.style.pointerEvents = 'none';
          // The row's real, current cart key — needed for the actual
          // /cart/change.js call, which the stable _unit token can't be used
          // for directly. Freshest at click time since the row is re-rendered
          // from the server after every change.
          const realKey = row.dataset.sariRealKey || sariKey;
          removeLinkedAddons(sariKey)
            .then(() => cartRemoveByKey(realKey))
            .then((result) => applySections(result && result.sections, sariKey, result && result.item_count))
            .catch((e) => console.error(e));
        });
      }
    });

    // Editable customization controls on each sari cart line.
    document.querySelectorAll('.sari-customize').forEach((scope) => {
      const sariKey = scope.dataset.sariKey;

      // Expand / collapse toggle. Re-applied on every bind so the panel keeps
      // its open state through a cart section re-render.
      const toggleBtn = scope.querySelector('[data-sari-customize-toggle]');
      const list = scope.querySelector('[data-sari-customize-list]');
      if (toggleBtn && list) {
        const setExpanded = (expanded) => {
          toggleBtn.setAttribute('aria-expanded', String(expanded));
          list.hidden = !expanded;
        };
        setExpanded(expandedCustomize.has(sariKey));
        if (!toggleBtn.dataset.bound) {
          toggleBtn.dataset.bound = 'true';
          toggleBtn.addEventListener('click', () => {
            const open = !expandedCustomize.has(sariKey);
            if (open) expandedCustomize.add(sariKey);
            else expandedCustomize.delete(sariKey);
            setExpanded(open);
          });
        }
      }

      if (scope.dataset.editBound) return;
      scope.dataset.editBound = 'true';

      // Stop the change event reaching Dawn's <cart-items> handler, which would
      // call updateQuantity() with no line index and lock the cart.
      scope.addEventListener('change', (event) => event.stopPropagation());
      scope.addEventListener('input', (event) => event.stopPropagation());

      const master = scope.querySelector('[data-blouse-master]');
      if (master) {
        master.addEventListener('change', () => {
          const radios = scope.querySelectorAll('[data-addon-radio]');
          if (master.checked) {
            if (!scope.querySelector('[data-addon-radio]:checked') && radios[0]) radios[0].checked = true;
          } else {
            radios.forEach((r) => (r.checked = false));
          }
          syncSari(scope, sariKey);
        });
      }

      scope.querySelectorAll('[data-addon-radio]').forEach((radio) => {
        radio.addEventListener('change', () => {
          if (master && radio.checked) master.checked = true;
          const row1Price = scope.querySelector('[data-blouse-row1-price]');
          if (row1Price && radio.dataset.price) row1Price.innerHTML = radio.dataset.price;
        });
      });

      scope.querySelectorAll('[data-addon-checkbox], [data-addon-radio]').forEach((input) => {
        input.addEventListener('change', () => syncSari(scope, sariKey));
      });
    });
  }

  document.addEventListener('DOMContentLoaded', bindHandlers);
  document.addEventListener('cart:refresh-needed', bindHandlers);
  window.SariCustomization = { bindHandlers };
})();
